import React, { useEffect, useRef, useState } from 'react';
import 'primeicons/primeicons.css';
import { carteraApi } from './api';
import { ModalPortal } from './ModalPortal';

/*
 * Modal de redacción para ENVIAR el estado de cuenta por correo (envío real vía
 * backend → MyMail tipo 7). Autocompleta el correo del cliente, permite editar
 * asunto/cuerpo/CC y adjunta el PDF (getPdfBlob) automáticamente.
 *
 * props: { open, onClose, creditoId, clave, cliente, defaultTo, defaultSubject,
 *          defaultBody, getPdfBlob, toast }
 */
export const EnviarEdoCuentaModal = ({
  open, onClose, creditoId, clave, cliente, defaultTo, defaultSubject, defaultBody, getPdfBlob,
}) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (open) {
      setTo(defaultTo || '');
      setCc([]);
      setCcInput('');
      setShowCc(false);
      setSubject(defaultSubject || '');
      setBody(defaultBody || '');
      setSending(false);
      setSent(false);
      setError(null);
    }
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, [open, defaultTo, defaultSubject, defaultBody]);

  if (!open) return null;

  const emailOk = /\S+@\S+\.\S+/.test(to.trim());
  const canSend = emailOk && subject.trim() && body.trim() && !sending;

  const addCc = () => {
    const v = ccInput.trim();
    if (v && /\S+@\S+\.\S+/.test(v) && !cc.includes(v)) { setCc([...cc, v]); setCcInput(''); }
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('to', to.trim());
      cc.forEach((c) => fd.append('cc[]', c));
      fd.append('subject', subject.trim());
      fd.append('content', body);
      // Adjuntar el PDF del estado de cuenta.
      if (getPdfBlob) {
        const blob = await getPdfBlob();
        if (blob) fd.append('attachments[]', new File([blob], `estado-cuenta-${clave || 'peicar'}.pdf`, { type: 'application/pdf' }));
      }
      await carteraApi.enviarEstadoCuentaEmail(creditoId, fd);
      setSent(true);
      closeTimer.current = setTimeout(() => onClose && onClose(), 1600);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  const stop = (e) => e.stopPropagation();
  const lockClose = sending ? () => {} : onClose;

  return (
    <ModalPortal>
    <div onClick={lockClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.32)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={stop} className="co-fade" style={{ background: '#fff', borderRadius: 20, width: 560, maxWidth: '100%', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(12,32,54,.32)' }}>
        {/* Header */}
        <div style={{ padding: '20px 22px', borderBottom: '1px solid #EEF2F5', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 13, background: '#E8EFF6', color: '#21588C', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="pi pi-envelope" style={{ fontSize: 18 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>Enviar estado de cuenta</div>
            <div className="tnum" style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>{cliente} · {clave}</div>
          </div>
          <div onClick={lockClose} className="co-row-hover" style={{ width: 32, height: 32, borderRadius: 10, background: '#F1F5F9', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <span className="pi pi-times" style={{ fontSize: 14 }} />
          </div>
        </div>
        <div style={{ height: 3, background: 'linear-gradient(90deg,#204B7B,#32B8DF,#00BA6E)' }} />

        {sent ? (
          <div style={{ padding: '46px 24px', textAlign: 'center' }}>
            <div style={{ width: 60, height: 60, borderRadius: 18, background: '#E5F8EF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <span className="pi pi-check-circle" style={{ fontSize: 26, color: '#00BA6E' }} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Correo enviado</div>
            <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>Se envió a {to}</div>
          </div>
        ) : (
          <div style={{ padding: '18px 22px' }}>
            {/* Para */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>Para</div>
              <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="correo@cliente.com"
                style={{ width: '100%', border: `1.5px solid ${to && !emailOk ? '#DC2626' : '#E7EBF0'}`, borderRadius: 10, padding: '10px 12px', fontSize: 13.5, color: '#0F172A', outline: 'none', boxSizing: 'border-box' }} />
              {!defaultTo && <div style={{ fontSize: 11, color: '#E5631B', marginTop: 4 }}>Este contrato no tiene correo registrado — escríbelo arriba.</div>}
            </div>

            {/* CC */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                <span style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>CC</span>
                {!showCc && <button type="button" onClick={() => setShowCc(true)} style={{ background: 'none', border: 'none', color: '#32B8DF', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>+ Agregar</button>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {cc.map((c, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#EFF6FB', color: '#21588C', fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 8 }}>
                    {c}<span onClick={() => setCc(cc.filter((_, idx) => idx !== i))} style={{ cursor: 'pointer', color: '#94A3B8' }}>×</span>
                  </span>
                ))}
                {showCc && (
                  <input autoFocus value={ccInput} onChange={(e) => setCcInput(e.target.value)} placeholder="correo@ejemplo.com + Enter"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCc(); } }}
                    onBlur={() => { addCc(); if (!ccInput) setShowCc(false); }}
                    style={{ flex: 1, minWidth: 160, border: '1.5px solid #E7EBF0', borderRadius: 10, padding: '8px 11px', fontSize: 13, outline: 'none' }} />
                )}
                {!showCc && cc.length === 0 && <span style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic' }}>Sin copia</span>}
              </div>
            </div>

            {/* Asunto */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>Asunto</div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                style={{ width: '100%', border: '1.5px solid #E7EBF0', borderRadius: 10, padding: '10px 12px', fontSize: 13.5, color: '#0F172A', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* Mensaje */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>Mensaje</div>
              <textarea value={body} onChange={(e) => setBody(e.target.value)}
                style={{ width: '100%', minHeight: 150, resize: 'vertical', border: '1.5px solid #E7EBF0', borderRadius: 12, padding: '11px 13px', fontSize: 13.5, lineHeight: 1.55, color: '#0F172A', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* Adjunto */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#F5F9FC', border: '1px solid #EAF0F5', borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
              <span style={{ width: 30, height: 30, borderRadius: 8, background: '#FBE7E7', color: '#DC2626', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><span className="pi pi-file-pdf" style={{ fontSize: 14 }} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0F172A' }}>estado-cuenta-{clave}.pdf</div>
                <div style={{ fontSize: 11, color: '#94A3B8' }}>Se adjunta automáticamente</div>
              </div>
              <span className="pi pi-paperclip" style={{ fontSize: 13, color: '#94A3B8' }} />
            </div>

            {error && <div style={{ fontSize: 12.5, color: '#DC2626', fontWeight: 600, marginBottom: 12 }}><span className="pi pi-exclamation-triangle" style={{ marginRight: 6 }} />{error}</div>}

            <div style={{ display: 'flex', gap: 9 }}>
              <button onClick={lockClose} disabled={sending} className="btn btn-ghost" style={{ flex: '0 0 auto' }}>Cancelar</button>
              <button onClick={handleSend} disabled={!canSend} className="btn btn-cyan" style={{ flex: 1, justifyContent: 'center', opacity: canSend ? 1 : 0.6 }}>
                {sending ? <><span className="pi pi-spin pi-spinner" />Enviando…</> : <><span className="pi pi-send" />Enviar correo</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </ModalPortal>
  );
};

export default EnviarEdoCuentaModal;
